import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { apiErrorMessage } from "../lib/inventoryApi";
import Hint from "../components/etams/Hint";

// Q32 — ambang temuan Papan Kontrol disetel OWNER, bukan hardcode. Baris
// region NULL = bawaan semua region; baris ber-region menimpanya. Kepala
// Cabang boleh MEMBACA (tahu kapan temuannya merah), tidak boleh mengubah —
// backend menolak PATCH selain Owner; tombol simpan hanya cermin.
const SATUAN = ["HARI_KERJA", "JAM", "KEJADIAN", "RUPIAH", "PERSEN"];
const LABEL_SATUAN = { HARI_KERJA: "Hari kerja", JAM: "Jam", KEJADIAN: "Kejadian", RUPIAH: "Rupiah", PERSEN: "Persen" };
// Nama jenis temuan yang dibaca orang — sejalan dengan LABEL_JENIS di PapanKontrol.jsx.
const LABEL_JENIS = {
  K13_TARIF_DIUBAH_SESUDAH_SERAH_TERIMA: "Tarif borongan diubah sesudah serah terima",
  Q20_SELISIH_SERAH_TERIMA: "Selisih serah terima antar tahap",
  Q21_WO_MENUNGGU_REMAKE: "WO berhenti menunggu remake",
  K108_UPDATE_LEWAT_TUTUP_HARI: "Update lewat tutup hari kerja",
  DOKU_TAUTAN_KEDALUWARSA: "Tautan bayar DOKU kedaluwarsa",
  BACKUP_TUGAS_TERJADWAL_GAGAL: "Backup / tugas terjadwal gagal",
  Q42_FA_LAMA_BERISIKO_TELAT: "FA lama berisiko telat",
  TARIF_BORONGAN_BELUM_LENGKAP: "Tarif borongan belum lengkap",
};

export default function PapanKontrolAmbang() {
  const { isOwner } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState({}); // id -> {ambang_angka, ambang_satuan, aktif, catatan}
  const [regions, setRegions] = useState([]);   // nama wilayah untuk kolom Wilayah (bukan potongan ID)
  useEffect(() => {
    api.get("/regions")
      .then((r) => setRegions(r.data?.items || r.data || []))
      .catch((e) => toast.error(apiErrorMessage(e, "Daftar wilayah gagal dimuat.")));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/papan-kontrol/ambang");
      setRows(r.data.items || []); setEdit({});
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat ambang")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ubah = (id, k, v) => setEdit((s) => ({ ...s, [id]: { ...(s[id] || {}), [k]: v } }));
  const simpan = async (row) => {
    const d = edit[row.id] || {};
    const body = {};
    if ("ambang_angka" in d) body.ambang_angka = d.ambang_angka === "" ? null : Number(d.ambang_angka);
    if ("ambang_satuan" in d) body.ambang_satuan = d.ambang_satuan || null;
    if ("aktif" in d) body.aktif = !!d.aktif;
    if ("catatan" in d) body.catatan = d.catatan || null;
    if (Object.keys(body).length === 0) return;
    try {
      await api.patch(`/papan-kontrol/ambang/${row.id}`, body);
      toast.success("Ambang disimpan"); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal menyimpan ambang")); }
  };

  return (
    <div className="space-y-6" data-testid="papan-kontrol-ambang-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          Ambang Papan Kontrol{" "}
          <Hint>Kapan sebuah temuan jadi KUNING / MERAH. Baris tanpa wilayah = bawaan; baris untuk satu wilayah menimpa bawaan.</Hint>
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{isOwner ? "Perubahan tercatat di Jejak Audit." : "Hanya Owner yang boleh mengubah."}</p>
      </div>
      <Card className="p-4">
        {loading ? <p className="text-sm text-muted-foreground">Memuat…</p> : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="pk-ambang-kosong">Belum ada baris ambang.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr><th className="text-left py-2 px-2">Jenis</th><th className="text-left px-2">Wilayah</th><th className="text-left px-2">Warna</th><th className="text-left px-2">Angka</th><th className="text-left px-2">Satuan</th><th className="text-left px-2">Aktif</th><th className="text-left px-2">Catatan</th><th></th></tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const d = edit[r.id] || {};
                  const angka = "ambang_angka" in d ? d.ambang_angka : (r.ambang_angka ?? "");
                  const satuan = "ambang_satuan" in d ? d.ambang_satuan : (r.ambang_satuan || "");
                  const aktif = "aktif" in d ? d.aktif : r.aktif !== false;
                  const catatan = "catatan" in d ? d.catatan : (r.catatan || "");
                  return (
                    <tr key={r.id} className="border-b border-border align-top" data-testid={`pk-ambang-${r.id}`}>
                      <td className="py-2 px-2 whitespace-nowrap">{LABEL_JENIS[r.jenis_temuan] || "—"}</td>
                      <td className="px-2 text-xs">{r.region_id ? (regions.find((x) => x.id === r.region_id)?.name || "—") : <Badge variant="outline">bawaan</Badge>}</td>
                      <td className="px-2">{r.warna === "MERAH" ? <Badge variant="destructive">MERAH</Badge> : <Badge variant="outline">KUNING</Badge>}</td>
                      <td className="px-2"><Input type="number" value={angka} disabled={!isOwner} onChange={(e) => ubah(r.id, "ambang_angka", e.target.value)} className="w-24" data-testid={`pk-ambang-angka-${r.id}`} /></td>
                      <td className="px-2">
                        <select value={satuan} disabled={!isOwner} onChange={(e) => ubah(r.id, "ambang_satuan", e.target.value)} className="border border-input rounded px-2 py-1.5 bg-background">
                          <option value="">—</option>{SATUAN.map((s) => <option key={s} value={s}>{LABEL_SATUAN[s]}</option>)}
                        </select>
                      </td>
                      <td className="px-2"><input type="checkbox" checked={aktif} disabled={!isOwner} onChange={(e) => ubah(r.id, "aktif", e.target.checked)} data-testid={`pk-ambang-aktif-${r.id}`} /></td>
                      <td className="px-2"><Input value={catatan} disabled={!isOwner} onChange={(e) => ubah(r.id, "catatan", e.target.value)} className="w-56" /></td>
                      <td className="px-2 text-right">{isOwner && <Button size="sm" variant="outline" disabled={!edit[r.id]} onClick={() => simpan(r)} data-testid={`pk-ambang-simpan-${r.id}`}>Simpan</Button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
